import axios from "axios"
import React, { useEffect, useState } from "react"
import GetCSFR from "../../../hooks/userAuthentication/GetCSFR"
import { useNavigate, useParams} from "react-router-dom"
import LoadingAnimation from "../../../components/general/LoadingAnimation"
import Header from "../../../components/navigation/Header"
import Footer from "../../../components/navigation/Footer"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLongArrowLeft } from "@fortawesome/free-solid-svg-icons"

interface Order {
    id: string;
    title: string;
    size: string;
    colour: string;
    quantity: number;
    image: string;
    price: number;
}

export default function MerchandisePreview(){
    const navigate = useNavigate()
    const [data,setData] = useState<any|null>(null)
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const { productId } = useParams()
    const [selectedImage, setSelectedImage] = useState<string>("")
    const [productSizeOptions, setProductSizeOptions] = useState<string[]>([])
    const [productColourOptions, setProductColourOptions] = useState<any[]>([])
    const [orderDetails, setOrderDetails] = useState<Order>()
    const [buttonMessage, setButtonMessage] = useState<string>('Add To Cart')
    
    // Fetch product details
    useEffect(() => {
        document.title = "SkillJa - Merchandise"

        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/merchandise/get_product/?product-id=${productId}`, {
            headers: {
            "X-CSRFToken": csrfToken,
            "Content-Type": "application/json",
            },
            withCredentials: true,
        })
        .then((res) => {
            if (res.status === 200) {
                setData(res.data.data)
                setSelectedImage(res.data.data.images[0].src)
                setOrderDetails((prev)=>({
                    ...prev, 
                    id: res.data.data.title, 
                    title: res.data.data.title, 
                    size: "",
                    quantity: 1,
                    colour: "",
                    image: res.data.data.images[0].src,
                    price: (res.data.data.variants[0].price / 100)
                }))

                // Manually set data for Tote Bag as Printify API is not structured well
                if(res.data.data.title.includes('Tote')){
                    setProductColourOptions([{id:'82432',title:'Natural', colors:['#ddd7cc']}])
                    setProductSizeOptions(['One size'])
                }
                else {
                    // Extract the colour name from each variant
                    const colourNames: string[] = Array.from(new Set(
                        res.data.data.variants
                            .filter((variant: any) => variant.title.length > 0 && variant.is_enabled === true)
                            .map((variant: any) => variant.title.split("/")[0].trim())
                    ))

                    // Extract the colour hex code from each variant 
                    const options: string[] = Array.from(new Set(
                        res.data.data.options[0].values
                            .filter((variant: any) => colourNames.includes(variant.title))
                            .map((variant: any) => variant )
                    ))
                    setProductColourOptions(options)
                    console.log(options)
                    // Extract the sizes from each variant 
                    const availableSizes: string[] = Array.from(new Set(
                        res.data.data.variants
                            .filter((variant: any) => variant.title.length > 0 && variant.is_enabled === true)
                            .map((variant: any) => variant.title.split("/")[1].trim())
                    ))
                    setProductSizeOptions(availableSizes)
                    console.log(availableSizes)
                }
            }
        })
        .catch((error) => { 
            alert('Error occurred! Please try again later.')
            console.error(error)
        })
    }, [productId])

    // Retrieve items saved in cart from cookies
    function getCart(): Order[] {
        const cookies = document.cookie.split("; ")
        const cartCookie = cookies.find(row => row.startsWith("cart="))
        
        try {
            return cartCookie ? JSON.parse(decodeURIComponent(cartCookie.split("=")[1])) : [];
        } catch (error) {
            console.error("Error parsing cart cookie:", error)
            return []
        }
    }
    
    // Add current item properties to cart
    function addToCart() {
        if (!orderDetails?.size || !orderDetails?.colour) {
            alert("Please select size and colour before adding to cart.")
            return
        }
    
        const cart = getCart();
        const existingItem = cart.find((item:any) => 
            item.id === orderDetails.id && 
            item.size === orderDetails.size && 
            item.colour === orderDetails.colour
        )
    
        if (existingItem) {
            existingItem.quantity += orderDetails.quantity
        } else {
            cart.push(orderDetails)
        }
        document.cookie = `cart=${encodeURIComponent(JSON.stringify(cart))}; path=/; max-age=86400`
    }
    
    return (
        <div>
            <Header useCase="merchandise" />
            {!data ? 
                <div className="my-20 mx-auto">
                    <LoadingAnimation />
                </div>
            :
                <section className="flex flex-wrap justify-center items-center lg:px-4 lg:mt-10 xl:px-8 mb-24 lg:mb-32">
                    <div className="flex flex-col justify-center items-center max-w-2xl mx-2 border-b-2 lg:border-0">
                        <div className="w-full">
                            <FontAwesomeIcon 
                                icon={faLongArrowLeft} 
                                onClick={()=>navigate('../')} 
                                className="text-3xl mb-auto mr-auto ml-0 pb-6 pl-2 lg:pb-0 lg:pl-0 hover:text-main-green-500 cursor-pointer" 
                            />
                        </div>
                        <img 
                            src={selectedImage} 
                            className="w-80 lg:w-6/12 xl:w-7/12 mx-3 border border-main-grey-200 rounded-lg"
                            tabIndex={0}
                        />
                        <div className="my-4 px-1 lg:px-3 lg:max-w-xl flex flex-wrap ">
                            {data.images.map((image:any,index:number)=>(
                                <img 
                                    key={index}
                                    src={image.src} 
                                    className="w-9 lg:w-12 m-1.5 rounded-lg border hover:border-main-grey-500 cursor-pointer" 
                                    onClick={()=>setSelectedImage(image.src)}
                                    tabIndex={0}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col justify-center items-center max-w-md font-kulim mt-4 lg:mt-0">
                        <h1 className="text-2xl font-medium font-kulim my-3">{data.title}</h1>
                        <h2 className="text-2xl">$ {data.variants[0].price / 100}</h2>
                        <p className="mt-6 px-4 text-center text-sm lg:text-base">{data.description.slice(3,-4)}</p>
                        <div className="flex flex-col justify-center items-center mt-10 font-kulim">
                            <p className="mb-3 mx-auto text-lg">Size</p>
                            <div className="flex">
                                {productSizeOptions.map((size:any,index:number)=>(
                                    <button 
                                        key={index} 
                                        className={`rounded-xl p-3 lg:w-16 ${size === 'One size' && 'lg:w-24'} border bg-white hover:border-main-green-500 mx-2 ${orderDetails?.size === size && 'border-2 border-main-color-gold'}`}
                                        tabIndex={0}
                                        onClick={()=> setOrderDetails((prev)=>({
                                            ...prev,
                                            size: size,
                                            colour: prev?.colour ?? "",
                                            quantity: prev?.quantity ?? 1,
                                            image: prev?.image ?? "",
                                            id: `${prev?.title}-${prev?.colour}-${size}`,
                                            title: prev?.title ?? "",
                                            price: prev?.price ?? 70
                                        }))}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex flex-col justify-center items-center mt-6">
                            <p className="mb-3 mx-auto text-lg">Colour</p>
                            <div className="flex flex-wrap">
                                {productColourOptions.map((colour:any,index:number)=>(
                                    <button 
                                        key={index} 
                                        tabIndex={0}
                                        style={{background:`${colour.colors[0]}`}} 
                                        className={`rounded-xl p-3 border bg-white mx-2 my-1 hover:border hover:border-main-color-gold ${orderDetails?.colour === colour.title && 'border-2 border-main-color-gold'}`}
                                        aria-label={`colour option ${colour.colors[0]}`}
                                        onClick={() => {
                                            setOrderDetails(prev => ({
                                                ...prev,
                                                size: prev?.size ?? "",
                                                colour: colour.title,
                                                quantity: prev?.quantity ?? 1,
                                                image: prev?.image ?? "",
                                                id: `${prev?.title}-${colour.title}-${prev?.size}`,
                                                title: prev?.title ?? "",
                                                price: prev?.price ?? 70
                                            }))
                                        }}
                                    >
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-center w-full mt-12">
                            <button 
                                className={`form-btn px-4 ${orderDetails?.colour && orderDetails?.size ? 'bg-main-green-500' :'bg-main-grey-200'} my-auto `}
                                aria-label="submit merchandise order"
                                disabled={orderDetails?.colour && orderDetails?.size ? false : true}
                                tabIndex={0}
                                onClick={()=>{
                                    addToCart()
                                    setButtonMessage("Success!")
                                    const timer = setTimeout(() => {
                                        setButtonMessage("Add To Cart")
                                    }, 1500);
                                    return ()=> clearTimeout(timer)
                                }}
                            >
                                {orderDetails?.colour && orderDetails?.size ?
                                    buttonMessage
                                :
                                    'Select Size & Colour'
                                }
                            </button>
                            <p className="ml-6 my-auto mr-3">x</p>
                            <input 
                                type="number"
                                min="1"
                                max="8"
                                value={orderDetails?.quantity ?? 1}
                                onChange={(e) => {
                                    const value = Math.max(1, Math.min(8, Number(e.target.value)));
                                    setOrderDetails(prev => prev ? { ...prev, quantity: value } : prev);
                                }}
                                className="border border-main-grey-100 text-xl text-center p-3 my-auto rounded-xl"
                            />
                        </div>
                    </div>
                </section>
            }
            <Footer />
        </div>
    )
}